import { TypedDocumentNode } from '@apollo/client';
import {
  ControllerRenderProps,
  MultiSelectOption,
  SelectOption,
  ServerSideMultiSelect,
  ServerSideMultiSelectProps,
} from '@otp/ui';
import {
  BaseFilter,
  BasePagination,
  BaseQueryProps,
  BaseSorting,
} from '@otp/ui/src/types/types';
import { DocumentNode } from 'graphql';
import React, { useEffect, useState } from 'react';
import {
  PaginationInput,
  User,
  UserFilterInput,
  UsersQuery,
} from 'src/graphql/generated/graphql';
import { useUsersLazy } from 'src/hooks/query/use-users-lazy';
import { getFullName } from 'src/utils/user-data';

type UsersProps<
  TOption extends SelectOption | MultiSelectOption,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  TData,
  TFilter extends BaseFilter,
  TPagination extends BasePagination,
  TSorting extends BaseSorting,
  TVariables extends BaseQueryProps<TQuery, TFilter, TPagination, TSorting>,
  TQuery extends DocumentNode | TypedDocumentNode<TData, TVariables>,
> = {
  field: ControllerRenderProps<any, any>;
  className?: string;
  userMap?: (item: Partial<User>) => TOption;
  filterGenerator?: (value: Partial<User> | Partial<User>[] | any) => {
    filter: UserFilterInput;
    pagination: PaginationInput;
  };
  multiple?: boolean;
} & Omit<
  ServerSideMultiSelectProps<
    TOption,
    Multiple,
    DisableClearable,
    FreeSolo,
    TData,
    TFilter,
    TPagination,
    TSorting,
    TVariables,
    TQuery
  >,
  | 'lazyQuery'
  | 'defaultPage'
  | 'defaultLimit'
  | 'allowSelectAll'
  | 'shouldGroup'
  | 'parseData'
>;

const DEFAULT_LIMIT = 20;

export const UsersServerSideField = <
  TOption extends SelectOption | MultiSelectOption,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  TData,
  TFilter extends BaseFilter,
  TPagination extends BasePagination,
  TSorting extends BaseSorting,
  TVariables extends BaseQueryProps<TQuery, TFilter, TPagination, TSorting>,
  TQuery extends DocumentNode | TypedDocumentNode<TData, TVariables>,
>({
  field,
  className,
  userMap = (user) =>
    ({
      value: user.id,
      name: getFullName(user, ' '),
    }) as TOption,
  filterGenerator = (value) => {
    const fieldValue = Array.isArray(value) ? value : [value];

    return {
      filter: {
        userIds: fieldValue,
      },
      pagination: {
        limit: fieldValue.length,
        offset: 0,
      },
    };
  },
  multiple,
}: UsersProps<
  TOption,
  Multiple,
  DisableClearable,
  FreeSolo,
  TData,
  TFilter,
  TPagination,
  TSorting,
  TVariables,
  TQuery
>) => {
  const [fetchUsers, { loading }] = useUsersLazy();
  const [selectedOptions, setSelectedOptions] = useState<TOption[]>([]);

  useEffect(() => {
    if (field.value) {
      const filter = filterGenerator(field.value);

      fetchUsers({
        variables: filter,
        onCompleted: (result) => {
          setSelectedOptions(result.users.records?.map(userMap) || []);
        },
      });
    }
  }, []);

  const resolvedValue = field.value
    ? selectedOptions.filter((option) =>
        Array.isArray(field.value)
          ? field.value.some((value) => value == option.value)
          : option.value == field.value,
      )
    : field.value;
  return (
    <>
      {!loading && (
        <ServerSideMultiSelect
          multiple={multiple}
          options={selectedOptions}
          lazyQuery={useUsersLazy}
          loading={loading}
          fullWidth={true}
          className={className}
          defaultLimit={DEFAULT_LIMIT}
          {...field}
          onChange={(event, value, reason, details) => {
            if (Array.isArray(value)) {
              field.onChange((value as TOption[]).map((val) => val.value));
              setSelectedOptions(value);
            } else if (value && typeof value === 'object') {
              field.onChange((value as TOption).value);
              setSelectedOptions([value]);
            } else {
              field.onChange(null);
              setSelectedOptions([]);
            }
          }}
          value={resolvedValue}
          ref={undefined}
          parseData={(data: UsersQuery, page: number, limit: number) => {
            return {
              results: data.users.records?.map(userMap) || [],
              hasNextPage: data.users.pagination.totalCount > page * limit,
            };
          }}
        ></ServerSideMultiSelect>
      )}
    </>
  );
};
